import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import { motion } from 'framer-motion';
import { Cookie, Settings, Info } from 'lucide-react';
import ParticleField from '../components/ThreeScene/ParticleField';
import { useMousePosition } from '../hooks/useMousePosition';
import { fadeInUp, staggerContainer } from '../utils/animations';

const CookiePolicy = () => {
  const mousePosition = useMousePosition();

  return (
    <div className="relative min-h-screen bg-dark-surface">
      {/* 3D Canvas Background */}
      <div className="absolute inset-0 z-0">
        <Canvas
          camera={{ position: [0, 0, 15], fov: 60 }}
          gl={{ antialias: true, alpha: true }}
        >
          <Suspense fallback={null}>
            <ambientLight intensity={0.3} />
            <pointLight position={[10, 10, 10]} intensity={0.8} />
            <ParticleField
              count={1500}
              radius={12}
              colors={['#6C5CE7', '#1F51FF']}
              mousePosition={mousePosition}
            />
            <OrbitControls
              enableZoom={false}
              enablePan={false}
              autoRotate
              autoRotateSpeed={0.2}
              maxPolarAngle={Math.PI / 2}
              minPolarAngle={Math.PI / 2}
            />
          </Suspense>
        </Canvas>
      </div>
      {/* Hero Section */}
      <section className="relative z-10 py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-dark-surface/80 to-dark-surface-100/80">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <Cookie size={64} className="text-neon-purple mx-auto mb-6" />
            <h1 className="text-h1 font-bold mb-4">
              <span className="gradient-text">Cookie Policy</span>
            </h1>
            <p className="text-body-lg text-text-secondary max-w-2xl mx-auto">
              Learn about how we use cookies and similar technologies to enhance your experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="space-y-12"
          >
            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Info className="text-neon-blue" size={24} />
                <h2 className="text-h3 font-bold">What Are Cookies</h2>
              </div>
              <p className="text-text-secondary">
                Cookies are small text files that are placed on your computer or mobile device 
                when you visit a website. They are widely used to make websites work more 
                efficiently and provide information to website owners.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Types of Cookies We Use</h2>
              <div className="space-y-4">
                <div className="border-l-4 border-neon-purple pl-4">
                  <h3 className="text-h4 font-semibold text-neon-purple mb-2">Essential Cookies</h3>
                  <p className="text-text-secondary">
                    These cookies are necessary for the website to function and cannot be switched off.
                  </p>
                </div>
                <div className="border-l-4 border-neon-blue pl-4">
                  <h3 className="text-h4 font-semibold text-neon-blue mb-2">Analytics Cookies</h3>
                  <p className="text-text-secondary">
                    Help us understand how visitors interact with our website by collecting information anonymously.
                  </p>
                </div>
                <div className="border-l-4 border-neon-purple pl-4">
                  <h3 className="text-h4 font-semibold text-neon-purple mb-2">Functional Cookies</h3>
                  <p className="text-text-secondary">
                    Enable enhanced functionality and personalization, such as remembering your preferences.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <Settings className="text-neon-purple" size={24} />
                <h2 className="text-h3 font-bold">Managing Cookies</h2>
              </div>
              <p className="text-text-secondary mb-4">
                You can control and manage cookies in various ways:
              </p>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Browser settings - Most browsers allow you to refuse cookies</li>
                <li>Third-party tools - Use privacy tools to manage tracking</li>
                <li>Opt-out links - Many advertising networks provide opt-out options</li>
                <li>Cookie preferences - Adjust settings in our cookie banner</li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Third-Party Cookies</h2>
              <p className="text-text-secondary">
                We may use third-party services that place cookies on your device. These include 
                analytics providers, advertising networks, and social media platforms. Each 
                third party has their own privacy policy governing their use of cookies.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-morphism p-8 rounded-2xl">
              <h2 className="text-h3 font-bold mb-4">Updates to This Policy</h2>
              <p className="text-text-secondary">
                We may update this Cookie Policy from time to time. Any changes will be posted 
                on this page with an updated revision date. For questions about our cookie usage, 
                contact us at <span className="text-neon-purple">cookies@starztech.com</span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Background Decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-1/4 w-96 h-96 bg-neon-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-1/4 w-96 h-96 bg-neon-blue/5 rounded-full blur-3xl" />
      </div>
    </div>
  );
};

export default CookiePolicy;